import { useEffect, useMemo, useState, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AsyncState } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Mail, Phone, Paperclip, User, ChevronDown, StickyNote, Search } from "lucide-react";

const STATUSES = ["New", "In Progress", "Waiting on Client", "Resolved", "Closed"];
const URGENCIES = ["Low", "Normal", "Urgent"];
const CATEGORIES = [
  "App Login Issue", "Booking Issue", "Billing / Payment Issue", "Package / Membership Question",
  "Cancellation / Rescheduling", "Mavs Volleyball Support", "Online Training Support",
  "Event / Camp Registration", "General Question", "Technical Issue", "Refund / Credit Request", "Other",
];
const statusColor = {
  New: "text-[hsl(199_89%_55%)]",
  "In Progress": "text-[hsl(48_96%_55%)]",
  "Waiting on Client": "text-[hsl(280_70%_65%)]",
  Resolved: "text-[hsl(142_60%_50%)]",
  Closed: "text-muted-foreground",
};
const urgencyColor = { Urgent: "bg-brand text-white", Normal: "bg-muted text-muted-foreground", Low: "bg-muted text-muted-foreground" };

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [ownerDraft, setOwnerDraft] = useState({});

  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("All");
  const [fCategory, setFCategory] = useState("All");
  const [fUrgency, setFUrgency] = useState("All");
  const [fOwner, setFOwner] = useState("All");

  const load = useCallback(() => { setLoading(true); api.get("/admin/support/tickets").then((r) => setTickets(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const owners = useMemo(() => Array.from(new Set(tickets.map((t) => t.assigned_owner).filter(Boolean))), [tickets]);

  const update = async (id, payload, msg = "Updated") => {
    try { await api.put(`/admin/support/tickets/${id}`, payload); toast.success(msg); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  const addNote = async (id) => {
    const note = (noteDraft[id] || "").trim();
    if (!note) return;
    await update(id, { internal_note: note }, "Note added");
    setNoteDraft((d) => ({ ...d, [id]: "" }));
  };

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (fStatus !== "All" && t.status !== fStatus) return false;
      if (fCategory !== "All" && t.category !== fCategory) return false;
      if (fUrgency !== "All" && t.urgency !== fUrgency) return false;
      if (fOwner !== "All") {
        if (fOwner === "unassigned" && (t.assigned_owner || "") !== "") return false;
        if (fOwner !== "unassigned" && t.assigned_owner !== fOwner) return false;
      }
      if (q) {
        const hay = `${t.name} ${t.email} ${t.subject} ${t.message} ${t.client_name}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [tickets, fStatus, fCategory, fUrgency, fOwner, q]);

  return (
    <AdminLayout>
      <h1 className="display-title text-4xl">Support Tickets</h1>
      <p className="mt-1 text-sm text-muted-foreground">{filtered.length} of {tickets.length} ticket(s)</p>

      {/* Filters */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets" data-testid="ticket-search" className="pl-9" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger data-testid="ticket-filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="All">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fCategory} onValueChange={setFCategory}><SelectTrigger data-testid="ticket-filter-category"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="All">All Categories</SelectItem>{CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fUrgency} onValueChange={setFUrgency}><SelectTrigger data-testid="ticket-filter-urgency"><SelectValue placeholder="Urgency" /></SelectTrigger><SelectContent><SelectItem value="All">All Urgency</SelectItem>{URGENCIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fOwner} onValueChange={setFOwner}><SelectTrigger data-testid="ticket-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger><SelectContent><SelectItem value="All">All Owners</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
      </div>

      <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No tickets" emptyDescription="No tickets match your filters.">
        <div className="mt-6 space-y-4">
          {filtered.map((t) => {
            const open = expanded === t.id;
            return (
              <div key={t.id} className="rounded-xl border border-border bg-card p-5" data-testid="admin-ticket-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("text-xs font-semibold uppercase tracking-wide", statusColor[t.status] || "")}>{t.status}</span>
                      <span className="text-xs text-muted-foreground">· {t.category}</span>
                      {t.urgency && <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase", urgencyColor[t.urgency] || "bg-muted")}>{t.urgency}</span>}
                      {t.assigned_owner && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" />{t.assigned_owner}</span>}
                    </div>
                    <h3 className="mt-1 font-condensed text-lg font-semibold">{t.subject}</h3>
                    <p className="text-sm text-muted-foreground">{t.name} · {t.email}{t.phone ? ` · ${t.phone}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={t.status} onValueChange={(v) => update(t.id, { status: v })}>
                      <SelectTrigger className="w-44" data-testid={`ticket-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => setExpanded(open ? null : t.id)} data-testid={`ticket-expand-${t.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"><ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} /></button>
                  </div>
                </div>

                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{t.message}</p>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {t.client_name && <span>Client/Athlete: {t.client_name}</span>}
                  {t.preferred_contact && <span>Prefers: {t.preferred_contact}</span>}
                  {t.attachment_url && <a href={t.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline"><Paperclip className="h-3 w-3" /> Attachment</a>}
                  <span>{new Date(t.created_at).toLocaleString()}</span>
                </div>

                {open && (
                  <div className="mt-4 space-y-4 border-t border-border pt-4">
                    {/* Assign owner + email */}
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[200px]">
                        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assign Owner</label>
                        <div className="mt-1 flex gap-2">
                          <Input value={ownerDraft[t.id] ?? (t.assigned_owner || "")} onChange={(e) => setOwnerDraft((d) => ({ ...d, [t.id]: e.target.value }))} placeholder="Owner name or email" data-testid={`ticket-owner-${t.id}`} />
                          <button onClick={() => update(t.id, { assigned_owner: ownerDraft[t.id] ?? (t.assigned_owner || "") }, "Owner assigned")} data-testid={`ticket-assign-${t.id}`} className="rounded-md bg-brand px-4 text-sm font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors">Assign</button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href={`mailto:${t.email}?subject=${encodeURIComponent("Re: " + (t.subject || "Your support ticket"))}`} data-testid={`ticket-email-${t.id}`} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Mail className="h-4 w-4 text-brand" /> Reply by Email</a>
                        {t.phone && <a href={`tel:${t.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Phone className="h-4 w-4 text-brand" /> Call</a>}
                      </div>
                    </div>

                    {/* Internal notes */}
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><StickyNote className="h-3.5 w-3.5" /> Internal Notes</p>
                      {Array.isArray(t.internal_notes) && t.internal_notes.length > 0 && (
                        <ul className="mt-2 space-y-2">
                          {t.internal_notes.map((n) => (
                            <li key={n.at || n.note} className="rounded-md bg-muted/40 p-3 text-sm">
                              <p className="text-foreground/90">{n.note}</p>
                              <p className="mt-1 text-[11px] text-muted-foreground">{n.author} · {n.at ? new Date(n.at).toLocaleString() : ""}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-2 flex gap-2">
                        <Textarea rows={2} value={noteDraft[t.id] || ""} onChange={(e) => setNoteDraft((d) => ({ ...d, [t.id]: e.target.value }))} placeholder="Add an internal note (staff only)" data-testid={`ticket-note-input-${t.id}`} />
                        <button onClick={() => addNote(t.id)} data-testid={`ticket-note-add-${t.id}`} className="shrink-0 rounded-md bg-brand px-4 text-sm font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors">Add</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AsyncState>
    </AdminLayout>
  );
}
